import type { LisNamedPath, LisPreMap } from '@/api/lisConfigApi'

/**
 * Rules for the LIS Mapping tab, kept pure so they are testable (vitest runs
 * `.ts` only).
 *
 * A mapping's Source and Value fields are the message's top-level fields or
 * pre-mappings: a nested path (`observations[*].code.code`) is named once on the
 * Pre-Mapping tab (`code`) and mappings read it by that name. The backend
 * refuses to save anything else (`assertNestedPathsNamed` in cgt-backend's
 * lis-mapping.service.ts); this is the same rule, so the screen can say so
 * before a save is attempted, and can point out mappings saved before it.
 */

/** Inside something: a `.` or `[` in the path — the negation of `flattenExample`'s `topLevel`. */
export function isNestedPath(path: string): boolean {
  return /[.[]/.test(path)
}

/** The nested paths among `paths` that no pre-mapping of the source names, in order, once each. */
export function unnamedNestedPaths(
  paths: readonly (string | null | undefined)[],
  namedPaths: readonly Pick<LisNamedPath, 'path'>[],
): string[] {
  const named = new Set(namedPaths.map((n) => n.path))
  const out: string[] = []
  for (const p of paths) {
    if (p && isNestedPath(p) && !named.has(p) && !out.includes(p)) out.push(p)
  }
  return out
}

/** One form a source's mappings write to, for the "Forms with mappings" list. */
export interface MappedForm {
  formDefinitionId: string
  /** Destinations on this form — one per form field or table cell written. */
  destinations: number
  /** The Expected values that lead here (test codes, usually), sorted, once each. */
  expectedValues: string[]
  /** Destinations reading a nested path that is not a pre-mapping. */
  needsPreMapping: number
}

/**
 * Every form the source's mappings write to, most destinations first.
 *
 * Built from the pre-maps the tab already holds, so the list cannot disagree
 * with the per-form table below it.
 */
export function formsWithMappings(
  preMaps: readonly LisPreMap[],
  schemaId: string,
  namedPaths: readonly Pick<LisNamedPath, 'path'>[],
): MappedForm[] {
  const byForm = new Map<string, { destinations: number; expected: Set<string>; needsPreMapping: number }>()
  for (const preMap of preMaps) {
    if (preMap.schemaId !== schemaId) continue
    const unnamed =
      unnamedNestedPaths([...preMap.keys.map((k) => k.path), preMap.valuePath], namedPaths).length > 0
    for (const m of preMap.mappings) {
      const entry = byForm.get(m.formDefinitionId) ?? { destinations: 0, expected: new Set<string>(), needsPreMapping: 0 }
      entry.destinations += 1
      if (unnamed) entry.needsPreMapping += 1
      for (const k of preMap.keys) if (k.expectedValue) entry.expected.add(k.expectedValue)
      byForm.set(m.formDefinitionId, entry)
    }
  }
  return [...byForm]
    .map(([formDefinitionId, e]) => ({
      formDefinitionId,
      destinations: e.destinations,
      expectedValues: [...e.expected].sort((a, b) => a.localeCompare(b)),
      needsPreMapping: e.needsPreMapping,
    }))
    .sort((a, b) => b.destinations - a.destinations || a.formDefinitionId.localeCompare(b.formDefinitionId))
}
